import create from "zustand";
import { produce } from "immer";
import _ from "lodash";
import Deferred from "promise-deferred";
import { v4 as uuidv4 } from "uuid";
import Axios from "../apis/axios";
import { NodeId } from "components/NodeId";
import mime from "mime";

interface CommandObject {
  // Define your CommandObject properties here
}

interface CommandQueueSlice {
  commandQueue: CommandObject[];
  isCommandQueueProcessing: boolean;
  pauseCommandQueueProcessing: boolean;
  InProcess: any; // Update with appropriate type

  enqueueCommands: (
    commandObjects: CommandObject[]
  ) => Promise<void[] | undefined>;
  getCommand: (nodeId: string, type: string) => CommandObject | undefined;
  updateCommand: (commandObject: CommandObject) => void;
  deleteCommand: (nodeId: string, commandType: string) => boolean;
  pauseCommandQueue: () => Promise<void>;
  resumeCommandQueue: () => Promise<void>;
  clearCommandQueue: () => Promise<void>;
  processCommandQueue: () => Promise<void>;
  processCommand: (commandObject: CommandObject) => Promise<any>;
  executeTask: (commandObject: CommandObject) => Promise<any>;
  setCommandQueueOnlineState: (isAuthUser: boolean) => void;
  getUploadUrls: (commandObject: CommandObject) => Promise<void>;
  uriToBlob: (uri: string) => Promise<Blob>;
  uploadFiles: (commandObject: CommandObject) => Promise<void>;
}
const createCommandQueueSlice = (set: (data: any) => void, get: () => any) => ({
  commandQueue: [],
  isCommandQueueProcessing: false,
  pauseCommandQueueProcessing: false,
  InProcess: undefined,

  enqueueCommands: async (commandObjects: any) => {
   

    const isAuthUser = get().isAuthUser;
    const promises = [];

    for (const commandObject of commandObjects) {
      commandObject.id = uuidv4();
      if (isAuthUser === true) {
        const promise = new Deferred();
        commandObject.promise = promise;
        promises.push(promise.promise);
      }
    }

    set(
      produce((state: any) => {
        state.commandQueue.push(...commandObjects);
      })
    );

    if (
      get().isCommandQueueProcessing === false &&
      get().pauseCommandQueueProcessing === false &&
      isAuthUser === true
    ) {
      set({ isCommandQueueProcessing: true });
      get().processCommandQueue();
    }

    if (promises.length > 0) {
      return promises;
    } else {
      return;
    }
  },

  getCommand: (nodeId: string, type: any) => {
    const commandObjectIndex = get().commandQueue.findIndex(
      (item: any) => item.nodeId === nodeId && item.type === type
    );
    if (commandObjectIndex >= 0) {
      return get().commandQueue[commandObjectIndex];
    } else {
      return undefined;
    }
  },

  updateCommand: (commandObject: any) => {
   
    set(
      produce((state: any) => {
        const commandObjectIndex = state.commandQueue.findIndex(
          (item: any) => item.id === commandObject.id
        );
        state.commandQueue[commandObjectIndex] = commandObjectIndex;
      })
    );
  },

  deleteCommand: (nodeId: string, commandType: any) => {
    
    let returnValue = false;

    set(
      produce((state: any) => {
        if (commandType === "create") {
          //remove all command objects the have the deleted objects as parent
          const dependentCommandObjectIndex = state.commandQueue.findIndex(
            (item: any) => item.parentNodeIds.includes(nodeId)
          );
          if (dependentCommandObjectIndex >= 0) {
            state.commandQueue.splice(dependentCommandObjectIndex, 1);
          }
        }

        //remove command object from queue
        let commandObjectIndex = state.commandQueue.findIndex(
          (item: any) => item.nodeId === nodeId && item.type === commandType
        );
        if (commandObjectIndex >= 0) {
          state.commandQueue.splice(commandObjectIndex, 1);
          returnValue = true;
        }

        //remove any other command type object from queue
        commandObjectIndex = state.commandQueue.findIndex(
          (item: any) => item.nodeId === nodeId
        );
        if (commandObjectIndex >= 0) {
          state.commandQueue.splice(commandObjectIndex, 1);
        }
      })
    );

    return returnValue;
  },

  pauseCommandQueue: async () => {
    
    set({ pauseCommandQueueProcessing: false });
  },

  resumeCommandQueue: async () => {
    
    set({ pauseCommandQueueProcessing: false });
    if (
      get().isCommandQueueProcessing === false &&
      get().pauseCommandQueueProcessing === false &&
      get().isAuthUser === true
    ) {
      set({ isCommandQueueProcessing: true });
      get().processCommandQueue();
    }
  },

  clearCommandQueue: async () => {
   
    set({
      pauseCommandQueueProcessing: false,
      isCommandQueueProcessing: false,
      InProcess: undefined,
      commandQueue: [],
    });
  },

  processCommandQueue: async () => {
    

    const commandQueue = get().commandQueue;
    
    if (commandQueue.length > 0) {
      //get the command
      const commandObject = commandQueue[0];

      try {
        //execute the command
        //make sure we are still online
        if (get().isAuthUser === true) {
          //process the command
          set({ commandInProcess: commandObject });
         
          const returnValue = await get().processCommand(commandObject);
          set({ commandInProcess: undefined });

          
          //notify caller
          if (
            commandObject.promise?.promise !== undefined &&
            commandObject.promise?.promise !== null
          ) {
            commandObject.promise.resolve(returnValue);
          }

          //remove the command from the queue
          let commandQueueLength: any;
          set(
            produce((state: any) => {
              const commandObjectIndex = state.commandQueue.findIndex(
                (item: any) => item.id === commandObject.id
              );
              state.commandQueue.splice(commandObjectIndex, 1);
              commandQueueLength = state.commandQueue.length;
            })
          );

          //check if there is something more to process
          const commandQueue = get().commandQueue;
          if (
            commandQueueLength > 0 &&
            get().pauseCommandQueueProcessing === false &&
            get().isAuthUser === true
          ) {
            //there are further commands, so process
            get().processCommandQueue();
          } else {
            //nothing more to do
            set({ isCommandQueueProcessing: false });
          }
        } else {
          //stop processing
          set({ isCommandQueueProcessing: false });
          return;
        }
      } catch (error) {
        
        if (
          commandObject.promise?.promise !== undefined &&
          commandObject.promise?.promise !== null
        ) {
          commandObject.promise.reject(error);
        }
        set({ isCommandQueueProcessing: false, commandInProcess: undefined });
      }
    } else {
      set({ isCommandQueueProcessing: false, commandInProcess: undefined });
    }
  },

  processCommand: async (commandObject_: any) => {
    

    //TODO
    const commandObject = _.cloneDeep(commandObject_);

    //check if there are files to upload
    if (commandObject.filesToUpload?.length > 0) {
     
      if (commandObject.uploadUrlsReceived === undefined) {
        commandObject.uploadUrlsReceived = false;
      }

      //so get the upload urls
      if (
        get().isAuthUser === true &&
        commandObject.uploadUrlsReceived === false
      ) {
        try {
          await get().getUploadUrls(commandObject);
          commandObject.uploadUrlsReceived = true;
        } catch (error) {
        
          throw new Error(String(error));
        }
      } else {
       
        throw new Error();
      }

      //now upload the files
      if (get().isAuthUser === true) {
        try {
          await get().uploadFiles(commandObject);
        } catch (error) {
         
          throw new Error(String(error));
        }
      } else {
        
        throw new Error();
      }
    } else {
     
      commandObject.allFilesUploaded = true;
    }
   

    if (get().isAuthUser === true && commandObject.allFilesUploaded === true) {
      const returnValue = await get().executeTask(commandObject);
     
      return returnValue;
    } else {
     
      throw new Error();
    }
  },

  executeTask: async (commandObject: any) => {
    switch (commandObject.task) {
      case "processCreateNotificationCommand":
        return get().processCreateNotificationCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processUpdateNotificationCommand":
        return get().processUpdateNotificationCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processDeleteNotificationCommand":
        return get().processDeleteNotificationCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processCreateAttachmentCommand":
        return get().processCreateAttachmentCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateAttachmentCommand":
        return get().processUpdateAttachmentCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateGenericAttachmentCommand":
        return get().processUpdateGenericAttachmentCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processDeleteAttachmentCommand":
        return get().processDeleteAttachmentCommand(_.cloneDeep(commandObject));
        break;

      case "processCreateWorkflowCommand":
        return get().processCreateWorkflowCommand(_.cloneDeep(commandObject));
        break;

      case "processDeleteWorkflowCommand":
        return get().processDeleteWorkflowCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateWorkflowCommand":
        return get().processUpdateWorkflowCommand(_.cloneDeep(commandObject));
        break;

      case "processCreateFaqCommand":
        return get().processCreateFaqCommand(_.cloneDeep(commandObject));
        break;

      case "processDeleteFaqCommand":
        return get().processDeleteFaqCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateFaqCommand":
        return get().processUpdateFaqCommand(_.cloneDeep(commandObject));
        break;

      case "processCreateCounterCommand":
        return get().processCreateCounterCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateCounterCommand":
        return get().processUpdateCounterCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateCounterValueCommand":
        return get().processUpdateCounterValueCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processCreateReportConfigCommand":
        return get().processCreateReportConfigCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processCreateSiteConfigCommand":
        return get().processCreateSiteConfigCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateSiteConfigCommand":
        return get().processUpdateSiteConfigCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateAreaConfigCommand":
        return get().processUpdateAreaConfigCommand(_.cloneDeep(commandObject));
        break;

      case "processCreateAreaConfigCommand":
        return get().processCreateAreaConfigCommand(_.cloneDeep(commandObject));
        break;

      case "processDeleteAreaConfigCommand":
        return get().processDeleteAreaConfigCommand(_.cloneDeep(commandObject));
        break;

      case "processDeleteReportConfigCommand":
        return get().processDeleteReportConfigCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processDeleteBackupCommand":
        return get().processDeleteBackupCommand(_.cloneDeep(commandObject));
        break;

      case "processDeleteSiteConfigCommand":
        return get().processDeleteSiteConfigCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateReportConfigCommand":
        return get().processUpdateReportConfigCommand(
          _.cloneDeep(commandObject)
        );
        break;

        case "processUpdateReportConfigCommandFlag":
          return get().processUpdateReportConfigCommandFlag(
            _.cloneDeep(commandObject)
          );
          break;

          case "processExecuteReportConfiguration":
            return get().processExecuteReportConfiguration(
              _.cloneDeep(commandObject)
            );
            break;

      case "processCreateAttachmentCommand":
        return get().processCreateAttachmentCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateUserProfile":
        return get().processUpdateUserProfile(_.cloneDeep(commandObject));
        break;

      case "processUpdateUser":
        return get().processUpdateUser(_.cloneDeep(commandObject));
        break;

      case "processDeleteUser":
        return get().processDeleteUser(_.cloneDeep(commandObject));
        break;

      case "processUpdateEquipmentCommand":
        return get().processUpdateEquipmentCommand(_.cloneDeep(commandObject));
        break;

      case "processCreateEqConfigCommand":
        return get().processCreateEqConfigCommand(_.cloneDeep(commandObject));
        break;

      case "processDeleteEqConfigCommand":
        return get().processDeleteEqConfigCommand(_.cloneDeep(commandObject));
        break;

      case "processUpdateTypeNamePlateCommand":
        return get().processUpdateTypeNamePlateCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processUpdateCostCommand":
        return get().processUpdateCostCommand(_.cloneDeep(commandObject));
        break;

      case "processCreateMaintenanceCommand":
        return get().processCreateMaintenanceCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processDeleteMaintenanceTaskCommand":
        return get().processDeleteMaintenanceTaskCommand(
          _.cloneDeep(commandObject)
        );
        break;

      case "processUpdateMaintenanceCommand":
        return get().processUpdateMaintenanceCommand(
          _.cloneDeep(commandObject)
        );
        break;

      default:
        throw new Error("Task " + commandObject.task + " not found");
        break;
    }
  },

  getUploadUrls: async (commandObject: any) => {

    const files = [];
    for (const fileToUpload of commandObject.filesToUpload) {
      if (fileToUpload.nodeid === undefined) {
        const nodeId = new NodeId("mesutronic.de", uuidv4());
        fileToUpload.nodeid = nodeId.toUrl();
      }

      files.push({
        nodeid: fileToUpload.nodeid,
        extension: mime.getExtension(fileToUpload?.file?.mimetype),
      });
    }
    try {
      const response = await Axios.post(
        "api/v1/files",
        { extensions: files },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      if (response && response?.status == 200) {
       
        for (let index = 0; index < response.data.length; index++) {
          commandObject.filesToUpload[index].uploadInfo = {};
          commandObject.filesToUpload[index].uploadInfo.mimetype =
            commandObject.filesToUpload[index].file.mimetype;
          commandObject.filesToUpload[index].uploadInfo.fileid =
            response.data[index].fileid;
          commandObject.filesToUpload[index].uploadInfo.fileversion =
            response.data[index].fileversion;
          commandObject.filesToUpload[index].uploadInfo.bucketid =
            response.data[index].bucketid;
          commandObject.filesToUpload[index].uploadInfo.url =
            response.data[index].url;
          commandObject.filesToUpload[index].uploaded = false;
        }
      } else {
        throw new Error();
      }
    } catch (error) {
      throw new Error(String(error));
    }
  },

  uploadFiles: async (commandObject: any) => {
    set({
      allFilesUploaded: false
    })
    for (const fileToUpload of commandObject.filesToUpload) {
      if (get().isLoggedIn === true && fileToUpload.uploaded === false) {
        try {
          const file = await fetch(fileToUpload.file.localuri);
          const imgBlob = await file.blob();
         
          const response = await fetch(fileToUpload.uploadInfo.url, {
            method: "PUT",
            headers: {
              "Content-Type": "application/octet-stream",
              "Access-Control-Allow-Origin": "*",
            },
            body: imgBlob,
          });
          if (response && response?.status == 200) {
            fileToUpload.uploaded = true;
          } else {
            throw new Error();
          }
        } catch (error) {
         
          throw new Error(String(error));
        }
      }
    }

    //check that all files were uploaded
    let allFilesUploaded = true;
    for (const fileToUpload of commandObject.filesToUpload) {
      if (fileToUpload.uploaded === false) {
        allFilesUploaded = false;
      }
    }

    commandObject.allFilesUploaded = allFilesUploaded;

    set({
      allFilesUploaded: allFilesUploaded
    })
  },
});
export default createCommandQueueSlice;
